import React, {useState} from 'react';

import {UploadDocumentsModal} from 'app/docimport/components/UploadDocumentsModal';
import {ActionButton} from 'app/general/structures/SPPage';
import {
  DOCUMENT_CREATE_PAGE_URL,
  POLICIES_NEW_POLICY,
  PROCEDURES_ADD_NEW,
  PROCESS_CREATE_PAGE_URL,
  QUIZ_CREATE_PAGE_URL,
} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {capitalizeFirstLetter} from 'app/utils/strings';

import type {ImportableDocsContentType} from 'app/docimport/types';

type CreatableContentType =
  | 'procedure'
  | 'policy'
  | 'quiz'
  | 'proceduregroup'
  | 'document';

type Props = {
  disabled: boolean;
  contentType: CreatableContentType;
  disabledTooltip?: string;
};

export const CreateDocumentButton = ({
  disabled,
  contentType,
  disabledTooltip,
}: Props) => {
  const [isShowingModal, setIsShowingModal] = useState<boolean>(false);
  const [increment, setIncrement] = useState<number>(0);

  const LINK_LOOKUP = {
    procedure: PROCEDURES_ADD_NEW,
    policy: POLICIES_NEW_POLICY,
    proceduregroup: PROCESS_CREATE_PAGE_URL,
    document: DOCUMENT_CREATE_PAGE_URL,
    quiz: QUIZ_CREATE_PAGE_URL,
  };

  const ct = getContentType(contentType);

  const isImportable = (
    contentType: CreatableContentType
  ): contentType is ImportableDocsContentType =>
    contentType === 'procedure' || contentType === 'policy';

  return (
    <>
      <ActionButton
        size="sm"
        disabled={disabled}
        disabledTooltip={
          disabledTooltip ??
          gt(
            'You need higher than read-only access to a team to create %s',
            ct.pluralName
          )
        }
        text={gt('Create %s', capitalizeFirstLetter(ct.name))}
        icon="fa-plus-circle"
        link={LINK_LOOKUP[contentType]}
        dropdownItems={
          isImportable(contentType)
            ? [
                {
                  icon: 'fa-upload',
                  text: gt('Upload document'),
                  onClick: () => setIsShowingModal(true),
                },
              ]
            : null
        }
      />
      {isImportable(contentType) ? (
        <UploadDocumentsModal
          key={increment}
          targetContentType={contentType}
          show={isShowingModal}
          onClose={() => {
            setIsShowingModal(false);
            setIncrement((increment) => increment + 1);
          }}
        />
      ) : null}
    </>
  );
};
